<script lang="ts" setup>
import { Ory } from '@/plugins/ory'
import { useGlobalStore } from '@/stores/global-store'
import { eventBus } from './services/eventBusService'
import { useToast } from 'vuestic-ui/web-components'
import { CustomerPermissionsStore } from './stores/customerPermissionsStore'
import loadingGif from '@/assets/loading-citymesh.gif'
import { useI18n } from 'vue-i18n'
import { devDebug } from './helpers/errorHelper'
import { UserActionsStore } from './stores/userActionsStore'

const ORY_BASE_PATH = import.meta.env.VITE_APP_ORY_URL

const { t } = useI18n()
const isLoading: Ref<boolean> = ref(true)
const initError = ref(false)
const errorDisable = ref(false)

const globalStore = useGlobalStore()
const customerPermissionsStore = CustomerPermissionsStore()
const userActionsStore = UserActionsStore()

const { notify } = useToast()
eventBus.on('alertMessage', onAlertMessage)

function onAlertMessage(message: string) {
  notify({ message: message, color: 'warning' })
}

const handleVisibilityChange = () => {
  devDebug('document.visibilityState', document.visibilityState)
  if (document.visibilityState === 'visible' && !isLoading.value) {
    // Computer woke up or tab was focused.
    // Trigger the same init logic used on mount.
  }
}

async function checkActualInternet() {
  try {
    // We fetch a tiny image or a favicon with a cache-buster
    await fetch('https://www.citymesh.com/favicon.ico', {
      mode: 'no-cors',
      cache: 'no-store',
    })
    return true
  } catch (error) {
    return false // Request failed = no actual internet
  }
}

const initializeApp = async (retryCount = 0) => {
  devDebug(`## INITIALIZING APP (Attempt ${retryCount + 1}) ##`)

  // STATE MANAGEMENT
  // Only show the full error screen if we have exhausted initial retries
  if (retryCount === 0) {
    initError.value = false
  }

  // CONNECTION CHECK
  if ((await checkActualInternet()) === false) {
    devDebug('## Browser reports offline. Retrying...')
    if (retryCount < 5) {
      isLoading.value = true
      setTimeout(() => initializeApp(retryCount + 1), 2000)
    } else {
      initError.value = true
      isLoading.value = false
    }
    return
  }

  try {
    devDebug('## fetching session x')
    const { data: session } = await Ory.toSession()

    const isNewSession = globalStore.orySession?.identity?.id !== session?.identity?.id
    globalStore.setOrySession(session)
    await customerPermissionsStore.refresh(isNewSession)

    devDebug('## initialization successful')
    isLoading.value = false
    initError.value = false
  } catch (err: any) {
    // NETWORK ERROR / RACE CONDITION
    const isNetworkError = !err.response || err.code === 'ERR_NETWORK_CHANGED' || err.message === 'Network Error'

    if (isNetworkError && retryCount < 5) {
      devDebug(`## Network not ready (${err.message}). Retrying in 3s...`)
      // We do NOT set initError.value = true here yet.
      // We stay in the "isLoading" state.
      setTimeout(() => initializeApp(retryCount + 1), 3000)
      return
    }

    // AUTH FAILURE
    if (err.response?.status === 401 || err.response?.status === 403) {
      devDebug('## session invalid or expired - redirecting')

      isLoading.value = true
      initError.value = false

      globalStore.clearOrySession()
      customerPermissionsStore.clearStore()
      userActionsStore.clearUserActions()

      const returnTo = window.location.href
      // aal error?
      if (err.response?.status === 403) {
        window.location.href = `${ORY_BASE_PATH}/login?aal=aal2&return_to=${encodeURIComponent(returnTo)}`
      } else {
        window.location.href = `${ORY_BASE_PATH}/login?return_to=${encodeURIComponent(returnTo)}`
      }

      return
    }

    // FINAL FALLBACK (Real 500 errors or max retries)
    devDebug('## non-recoverable error')
    initError.value = true
    isLoading.value = false
  }
}

onMounted(async () => {
  initializeApp()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  eventBus.on('networkError', (type) => {
    devDebug(`## API Service reported network error: ${type} ##`)
    initError.value = true
  })
})

onUnmounted(() => {
  eventBus.off('alertMessage', onAlertMessage)
  eventBus.off('networkError')
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <div v-if="errorDisable" class="flex flex-col items-center justify-center min-h-[80vh] gap-6 px-4 text-center">
    <VaIcon name="wifi_off" size="large" color="danger" />
    <div>
      <h2 class="text-xl font-bold mb-2">{{ t('global.connection_error_title') }}</h2>
      <p class="text-secondary max-w-md">
        {{ t('global.connection_error_message') }}
      </p>
    </div>
    <VaButton preset="primary" icon="refresh" @click="initializeApp(0)">
      {{ t('global.retry_connection') }}
    </VaButton>
  </div>

  <div v-else-if="isLoading" class="flex flex-col items-center justify-center min-h-[80vh] gap-6 px-4">
    <img :src="loadingGif" width="54" data-bit="iit" />
    <p class="animate-pulse">{{ t('global.loading') }}...</p>
  </div>

  <RouterView v-else />
</template>

<style lang="scss">
@import 'scss/main.scss';

#app {
  font-family: 'neue-haas-grotesk-display', 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  margin: 0;
  min-width: 20rem;
}
</style>
